import { Input, Skeleton } from '@zyxantech/tux'
import { Box, ChevronDown, ChevronRight, Folder, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useGetAssetTreeQuery } from '@/api/assets.api'
import { ErrorState } from '@/components/shared/ErrorState'

/** Recursive asset node — the tree endpoint nests children to arbitrary depth. */
export interface TreeNode {
  id: string
  name: string
  type: string
  path: string
  depth: number
  children?: TreeNode[]
}

export interface AssetTreeProps {
  selectedId: string | undefined
  onSelect: (id: string) => void
}

// Static indent classes per depth (purge-safe literals). Deeper nodes clamp to
// the last entry — avoids dynamic class strings Tailwind would purge.
const INDENT_CLASS = [
  'pl-3',
  'pl-[26px]',
  'pl-[40px]',
  'pl-[54px]',
  'pl-[68px]',
  'pl-[82px]',
  'pl-[96px]',
] as const

function indentClass(depth: number): string {
  return INDENT_CLASS[Math.min(depth, INDENT_CLASS.length - 1)] ?? 'pl-3'
}

/** Filter the tree to nodes matching `q`, keeping ancestors of any match. */
function filterTree(nodes: TreeNode[], q: string): TreeNode[] {
  if (!q) return nodes
  const lower = q.toLowerCase()
  const walk = (node: TreeNode): TreeNode | null => {
    const matchedChildren = (node.children ?? []).map(walk).filter((n): n is TreeNode => n !== null)
    const selfMatch = node.name.toLowerCase().includes(lower)
    if (selfMatch || matchedChildren.length > 0) {
      return { ...node, children: matchedChildren }
    }
    return null
  }
  return nodes.map(walk).filter((n): n is TreeNode => n !== null)
}

export function AssetTree({ selectedId, onSelect }: AssetTreeProps) {
  const { data, isLoading, error, refetch } = useGetAssetTreeQuery()
  const [search, setSearch] = useState('')

  const tree = useMemo(() => {
    const roots = (data ?? []) as TreeNode[]
    return filterTree(roots, search)
  }, [data, search])

  return (
    <div className="flex h-full w-[280px] shrink-0 flex-col border-r border-border-subtle bg-bg-surface">
      <div className="border-b border-border-subtle p-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search assets…"
          leadingIcon={<Search size={14} />}
        />
      </div>

      <div className="flex-1 overflow-auto py-2">
        {isLoading && (
          <div className="space-y-2 px-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={24} />
            ))}
          </div>
        )}
        {error && <ErrorState error={error} retry={() => void refetch()} />}
        {!isLoading && !error && tree.length === 0 && (
          <div className="px-4 py-6 text-center text-xs text-text-muted">No assets found.</div>
        )}
        {tree.map((node) => (
          <TreeBranch
            key={node.id}
            node={node}
            depth={0}
            selectedId={selectedId}
            onSelect={onSelect}
            forceOpen={search.length > 0}
          />
        ))}
      </div>
    </div>
  )
}

interface TreeBranchProps {
  node: TreeNode
  depth: number
  selectedId: string | undefined
  onSelect: (id: string) => void
  forceOpen: boolean
}

function TreeBranch({ node, depth, selectedId, onSelect, forceOpen }: TreeBranchProps) {
  const [open, setOpen] = useState(depth < 1)
  const expanded = forceOpen || open
  const children = node.children ?? []
  const hasChildren = children.length > 0
  const isSelected = node.id === selectedId

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(node.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onSelect(node.id)
        }}
        className={`flex cursor-pointer items-center gap-1.5 py-1.5 pr-3 text-[13px] transition-colors ${indentClass(
          depth,
        )} ${
          isSelected
            ? 'border-l-2 border-accent bg-accent-dim text-accent'
            : 'border-l-2 border-transparent text-text-secondary hover:bg-bg-hover'
        }`}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setOpen((v) => !v)
            }}
            className="flex shrink-0 text-text-muted"
            tabIndex={-1}
          >
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        ) : (
          <span className="w-[13px] shrink-0" />
        )}
        {hasChildren ? (
          <Folder size={14} className="shrink-0" />
        ) : (
          <Box size={14} className="shrink-0" />
        )}
        <span className={`truncate ${isSelected ? 'text-accent' : 'text-text-primary'}`}>
          {node.name}
        </span>
        {hasChildren && (
          <span className="font-mono ml-auto text-[11px] text-text-muted">{children.length}</span>
        )}
      </div>
      {hasChildren && expanded && (
        <div>
          {children.map((child) => (
            <TreeBranch
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              forceOpen={forceOpen}
            />
          ))}
        </div>
      )}
    </div>
  )
}
